import { useEffect, useState, useCallback } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { scopeFilter, fetchUnreadTotal, FEED_ITEMS_CHANGED_EVENT } from "@/lib/feedReader";

const REFRESH_INTERVAL_MS = 60_000;

/**
 * Returns the total unread feed-reader items across the user's current scope
 * (single workspace, shared pool, or per-workspace silo — same rules as the page).
 */
export const useFeedReaderUnreadCount = () => {
  const { active, workspaces, libraryMode, loading: wsLoading } = useWorkspace();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (wsLoading) return;
    const ids = scopeFilter(libraryMode, active, workspaces);
    if (!ids.length) {
      setCount(0);
      setLoading(false);
      return;
    }
    try {
      const total = await fetchUnreadTotal(ids);
      setCount(total);
    } catch {
      // Swallow — badge is non-critical UI.
    } finally {
      setLoading(false);
    }
  }, [wsLoading, libraryMode, active, workspaces]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    const onFocus = () => void refresh();
    const onItemsChanged = () => void refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener(FEED_ITEMS_CHANGED_EVENT, onItemsChanged);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(FEED_ITEMS_CHANGED_EVENT, onItemsChanged);
    };
  }, [refresh]);

  return { count, loading };
};
